import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  ChevronLeft, ChevronRight, GalleryHorizontal,
} from 'lucide-react';
import { PlatformGlyph } from '@/components/publishing/PlatformGlyph';
import Layout from '@/components/Layout';
import {
  CalendarClipPickerDialog,
  type CalendarClipPick,
} from '@/components/publishing/CalendarClipPickerDialog';
import { ClipPublishDialogs } from '@/components/publishing/ClipPublishDialogs';
import { PublishQueueBadge } from '@/components/publishing/PublishQueueBadge';
import { ScheduledPublishSheet, type ScheduledSheetPublish } from '@/components/publishing/ScheduledPublishSheet';
import { Button } from '@/components/ui/button';
import { bucketPublishesByLocalDay, localDayKey, publishPlacementIso } from '@/lib/publishing/bucketPublishesByLocalDay';
import { buildCalendarPublishTarget } from '@/lib/publishing/buildCalendarPublishTarget';
import {
  buildCalendarMonthDays,
  calendarQueryRange,
  formatLocalTime,
  localYearMonth,
  resolveCalendarItemTitle,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import type { PublishDialogTarget } from '@/lib/publishing/types';
import { useHasFeature } from '@/lib/useHasFeature';
import { MY_SCHEDULED_PUBLISHES } from '@/queries/scheduledPublishing';

type SheetState =
  | { kind: 'closed' }
  | { kind: 'item'; publish: ScheduledSheetPublish };

const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const next = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
}

export const PublishCalendar = () => {
  const { t, i18n } = useTranslation();
  const timeZone = useMemo(() => viewerTimeZone(), []);
  const initial = useMemo(() => localYearMonth(timeZone, new Date()), [timeZone]);
  const [cursor, setCursor] = useState(initial);
  const [sheet, setSheet] = useState<SheetState>({ kind: 'closed' });
  const [pickerDayKey, setPickerDayKey] = useState<string | null>(null);
  const [publishTarget, setPublishTarget] = useState<PublishDialogTarget | null>(null);
  const [showPublishProGate, setShowPublishProGate] = useState(false);
  const [clipTitles, setClipTitles] = useState<Record<string, string>>({});
  const hasProFeatures = useHasFeature('pro_features');

  const range = useMemo(
    () => calendarQueryRange(cursor.year, cursor.month, timeZone),
    [cursor.year, cursor.month, timeZone],
  );
  const days = useMemo(
    () => buildCalendarMonthDays(cursor.year, cursor.month, timeZone),
    [cursor.year, cursor.month, timeZone],
  );
  const { data, loading, error, refetch } = useQuery(MY_SCHEDULED_PUBLISHES, {
    variables: { from: range.from, to: range.to },
    fetchPolicy: 'cache-and-network',
  });

  const buckets = useMemo(
    () => bucketPublishesByLocalDay(
      (data?.myScheduledPublishes || []) as ScheduledSheetPublish[],
      timeZone,
    ),
    [data?.myScheduledPublishes, timeZone],
  );

  const monthLabel = useMemo(() => {
    const sample = new Date(Date.UTC(cursor.year, cursor.month - 1, 15, 12));
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(sample);
  }, [cursor.year, cursor.month, i18n.language]);

  const weekdayLabels = useMemo(() => {
    const sunday = new Date(Date.UTC(2026, 7, 2, 12));
    return WEEKDAY_INDEXES.map((offset) => (
      new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' })
        .format(new Date(sunday.getTime() + offset * 86_400_000))
    ));
  }, [i18n.language]);

  const todayKey = useMemo(
    () => localDayKey(new Date().toISOString(), timeZone),
    [timeZone],
  );

  const openPublish = sheet.kind === 'item' ? sheet.publish : null;
  const itemPlacementIso = sheet.kind === 'item' ? publishPlacementIso(sheet.publish) : null;
  const sheetDayKey = itemPlacementIso ? localDayKey(itemPlacementIso, timeZone) : null;

  const handlePickClip = (pick: CalendarClipPick) => {
    if (!pickerDayKey) return;
    if (!hasProFeatures) {
      setShowPublishProGate(true);
      return;
    }
    setClipTitles((current) => ({ ...current, [pick.clipId]: pick.clipTitle }));
    setPublishTarget(buildCalendarPublishTarget(
      pick.clipId,
      pick.platform,
      pickerDayKey,
      timeZone,
    ));
    setPickerDayKey(null);
  };

  return (
    <Layout>
      <div className="w-full space-y-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{t('publishing.calendar.title')}</h1>
            <p className="mt-1 text-muted-foreground">{t('publishing.calendar.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              data-testid="calendar-prev-month"
              aria-label={t('publishing.calendar.prevMonth')}
              onClick={() => setCursor((current) => shiftMonth(current.year, current.month, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="min-w-40 text-center text-lg font-semibold capitalize" data-testid="calendar-month-label">
              {monthLabel}
            </h2>
            <Button
              type="button"
              variant="outline"
              size="icon"
              data-testid="calendar-next-month"
              aria-label={t('publishing.calendar.nextMonth')}
              onClick={() => setCursor((current) => shiftMonth(current.year, current.month, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {error ? (
          <p className="text-sm text-destructive">{t('publishing.calendar.error')}</p>
        ) : null}

        <div className="overflow-x-auto">
          <div className="grid min-w-[720px] grid-cols-7 gap-px overflow-hidden rounded-lg border bg-border">
            {weekdayLabels.map((label, index) => (
              <div
                key={index}
                className="bg-muted px-2 py-2 text-center text-xs font-medium text-muted-foreground"
              >
                {label}
              </div>
            ))}
            {days.map((day) => {
              const items = buckets.get(day.key) ?? [];
              const isToday = day.key === todayKey;
              return (
                <div
                  key={day.key}
                  data-testid={`calendar-day-${day.key}`}
                  className={`flex min-h-32 flex-col gap-1 bg-background p-2 ${
                    day.inCurrentMonth ? '' : 'bg-muted/40 text-muted-foreground'
                  } ${isToday ? 'ring-1 ring-inset ring-primary/40' : ''}`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-medium">{day.day}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-[11px]"
                      data-testid={`calendar-schedule-${day.key}`}
                      onClick={() => setPickerDayKey(day.key)}
                    >
                      {t('publishing.calendar.schedule')}
                    </Button>
                  </div>
                  <div className="flex flex-1 flex-col gap-1">
                    {items.map((item) => {
                      const placement = publishPlacementIso(item);
                      const itemTitle = resolveCalendarItemTitle(item)
                        || t('publishing.calendar.untitled');
                      const statusKey = `publishing.calendar.status.${item.status}`;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`calendar-item-${item.id}`}
                          className="group rounded-md border bg-card px-1.5 py-1 text-left hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setSheet({ kind: 'item', publish: item })}
                        >
                          <span className="flex items-start gap-1">
                            <PlatformGlyph platform={item.platform} />
                            {item.media_kind === 'carousel' ? (
                              <GalleryHorizontal
                                className="h-3 w-3 shrink-0"
                                aria-label={t('publishing.calendar.carousel')}
                                data-testid={`calendar-carousel-icon-${item.id}`}
                              />
                            ) : null}
                            <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                              {itemTitle}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-[10px] text-muted-foreground group-hover:text-accent-foreground/80">
                            {placement ? formatLocalTime(placement, timeZone, i18n.language) : ''}
                            {' · '}
                            {t(statusKey)}
                          </span>
                          {item.publish_queue?.name ? (
                            <PublishQueueBadge
                              name={item.publish_queue.name}
                              className="mt-0.5 text-[10px]"
                              testId={`calendar-queue-badge-${item.id}`}
                            />
                          ) : null}
                        </button>
                      );
                    })}
                    {loading && items.length === 0 ? (
                      <span className="text-[10px] text-muted-foreground">
                        {t('publishing.calendar.loading')}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <CalendarClipPickerDialog
        open={!!pickerDayKey}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPickerDayKey(null);
        }}
        dayKey={pickerDayKey}
        onPick={handlePickClip}
      />

      <ClipPublishDialogs
        showPublishProGate={showPublishProGate}
        onPublishProGateChange={setShowPublishProGate}
        publishTarget={publishTarget}
        onPublishTargetChange={setPublishTarget}
        resolveClipTitle={(clipId) => clipTitles[clipId] || ''}
        onPublished={() => { void refetch(); }}
      />

      <ScheduledPublishSheet
        open={sheet.kind !== 'closed'}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSheet({ kind: 'closed' });
        }}
        publish={openPublish}
        dayKey={sheetDayKey}
        timeZone={timeZone}
        onSaved={() => { void refetch(); }}
        onCancelled={() => { void refetch(); }}
      />
    </Layout>
  );
};

export default PublishCalendar;
